import React from 'react';
import { usePageTitle } from '../hooks/usePageTitle';

export interface PageShellProps {
  /** Names the page. Goes to the global header, and to the in-page heading. */
  title: string;
  /** One line under the title. */
  description?: string;
  /** Renders a back affordance in the global header. For detail pages. */
  backUrl?: string;
  /** Right-aligned page actions — "Create…", export, view toggles. */
  actions?: React.ReactNode;
  /** Contextual help key; renders a HelpTrigger beside the global-header title. */
  helpContentKey?: string;
  /**
   * Renders the title again as an in-page heading. OFF by default — see the
   * note on the component. Opt in for a page that reads better with a heading
   * of its own, or that has `actions` needing a row to sit in.
   */
  showHeading?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * P3-08. The scaffold every Pro page should sit in.
 *
 * Pro pages title themselves two incompatible ways, which is the rhythm problem
 * P3-08 describes. Checked in a browser:
 *
 *   Bills, Budget    title in the GLOBAL HEADER; page body has no heading
 *   Calendar         title in the PAGE BODY; global header is blank
 *
 * So it is not that pages go unnamed — it is that half of them name themselves
 * in a place the other half leaves empty, and moving between the two makes the
 * app feel like two apps. 45 of 75 pages register nothing at all, which is the
 * Calendar case.
 *
 * The header registration is therefore what this component is FOR, and it is
 * unconditional. The in-page heading is opt-in via `showHeading`, because the
 * dominant list-page pattern (Bills, Budget) deliberately has none — the header
 * carries the title and the content starts immediately. Defaulting it on would
 * have printed the title twice on exactly those pages.
 *
 * It generalises AdminPageShell, which 13 admin pages already use; that keeps
 * its heading by passing `showHeading`, so its pages are unchanged.
 */
const PageShell: React.FC<PageShellProps> = ({
  title,
  description,
  backUrl,
  actions,
  helpContentKey,
  showHeading = false,
  children,
  className = '',
}) => {
  usePageTitle(title, { description, backUrl, helpContentKey });

  const showHeadingRow = showHeading || !!actions;

  return (
    <div className={`space-y-4 ${className}`}>
      {showHeadingRow && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          {!showHeading ? (
            <span />
          ) : (
            <div className="min-w-0">
              <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary">
                {title}
              </h2>
              {description && (
                <p className="mt-0.5 text-sm text-text-tertiary dark:text-text-dark-tertiary">
                  {description}
                </p>
              )}
            </div>
          )}
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
};

export default PageShell;
